import { Box, BoxProps, ColorOptions, Loader, Spacer, TextNew as Text, TextNewProps as TextProps } from '@jfs/ui-new'
import { convertHexToRGBA } from '@jfs/ui-new/src/utils/utils'
import { useNavigation, useRoute } from '@react-navigation/native'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { CreditCard } from '../../comps/CreditCard'
import { CardType } from '../../data/models/Card'
import { EMIType } from '../../data/models/EMI'
import { useLazyApiData, useStores } from '../../data/store'
import { CardVendors, isIFrame } from '../../utils'
import { CARD_ELIGIBLE, EMI, PAYMENT_MODE_SUBTYPES, PAYMENT_MODE_TYPE, windowConfig } from '../../utils/constants'
import { staticStrings } from '../../utils/string'
import { MobileProcessing } from '../PaymentProcesseing/MobileProcessing'
import { CardEMIPlans } from './CardEMIPlans'
import { CCAllEMIPlans } from './CCAllEMIPlans'

const IS_SC_ENABLED = false
const Card: FC = () => {
  const {
    theme,
    getEMICardsEligibility,
    addPaymentMethod,
    binService,
    getEMISavedCards,
    amount,
    getCardEligibility,
    rightHeaderClicked,
    getCardEMIPlans,
    updateRightHeader,
    updateModalDisplaying,
    validateOffer,
    applyOffer,
    offerApplied,
    updateIsOfferApplied,
    getBankEmiId,
    isValidOffer,
    uniqueWindowId,
  } = useStores((root) => ({
    uniqueWindowId: root.config.uniqueWindowId,
    theme: root.config.merchantTheme,
    getEMICardsEligibility: root.getEMICardsEligibility,
    addPaymentMethod: root.transaction.addPaymentMethod,
    binService: root.binServiceApi,
    getEMISavedCards: root.getEMISavedCards,
    amount: root.getTransactionAmount(),
    getCardEligibility: root.getCardEligibility,
    getCardEMIPlans: root.getCardEMIPlans,
    rightHeaderClicked: root.config.rightHeaderClicked,
    updateRightHeader: root.config.updateRightHeaderClick,
    updateModalDisplaying: root.config.updateModalDisplaying,
    validateOffer: root.offerValidateApi,
    applyOffer: root.offerTxnApplyApi,
    offerApplied: root.isOfferApplied,
    isValidOffer: root.transaction.isValidOffer,
    updateIsOfferApplied: root.transaction.updateIsOfferApplied,
    getBankEmiId: root.getBankEMIID,
  }))

  const isOfferApplied = offerApplied(PAYMENT_MODE_SUBTYPES.CARD, PAYMENT_MODE_TYPE.EMI)
  const { REACT_APP_ENDPOINT = '', REACT_APP_URL = '' } = process.env

  const postDataRef = useRef<any>()

  const [showPaymentProcessModal, setShowPaymentProcessModal] = useState(false)
  const [emiPlansAvailable, setEMIPlansAvailable] = useState('')
  const [isEMISelected, setIsEMISelected] = useState(false)
  const [errMsg, setErrMsg] = useState('')
  const [errorColor, setErrorColor] = useState('')
  const [showCardEMI, setShowCardEMI] = useState(false)
  const [isCardEMIPlanSelected, setIsCardEMIPlanSelected] = useState(false)
  const [showAddeNew, setShowAddeNew] = useState(false)

  const emiSavedCards: any = getEMISavedCards()

  const [binServiceApi, { state: binServiceState, data: binServiceData }] = useLazyApiData(binService)
  const [lazyGetCardEligibility, { state: getCardEligibilityState, data: getCardEligibilityData }] =
    useLazyApiData(getCardEligibility)

  const [lazyApplyOffer, { state: applyOfferState }] = useLazyApiData(applyOffer)
  const [validateOfferApi, { state: validateOfferState }] = useLazyApiData(validateOffer)
  const [lazyGetCardsEligibility, { state: getCardsEligibilityState }] = useLazyApiData(getEMICardsEligibility)
  const { navigate } = useNavigation()
  const route: any = useRoute()

  const loadProcessingScreen = useCallback(() => {
    const loaderUrl = REACT_APP_ENDPOINT
      ? `${REACT_APP_URL}/${REACT_APP_ENDPOINT}/loader.html`
      : `${REACT_APP_URL}/loader.html`

    if (!isIFrame()) {
      window.currentChild = window
    } else {
      window.currentChild = window.open(loaderUrl, `jiopayWindow-${uniqueWindowId}`, windowConfig)
      window.currentChild?.focus()
    }
    setShowPaymentProcessModal(true)
  }, [REACT_APP_ENDPOINT, REACT_APP_URL, uniqueWindowId])

  useEffect(() => {
    if (IS_SC_ENABLED) {
      lazyGetCardsEligibility()
    }
  }, [lazyGetCardsEligibility])

  useEffect(() => {
    if (applyOfferState === 'fulfilled') {
      loadProcessingScreen()
    } else if (applyOfferState === 'rejected') {
      const cardNo = postDataRef.current.pan
      const schemeSubType = route?.params?.schemeSubType ?? '00'
      setErrMsg(staticStrings.offerError)
      lazyGetCardEligibility(cardNo, true, schemeSubType)
      setIsEMISelected(false)
      setEMIPlansAvailable('')
      setIsCardEMIPlanSelected(false)
    }
  }, [applyOfferState, lazyGetCardEligibility, loadProcessingScreen, route])

  useEffect(() => {
    if (validateOfferState === 'rejected') {
      setErrMsg(staticStrings.offerError)
    }
  }, [validateOfferState])

  useEffect(() => {
    if (getCardEligibilityState === 'fulfilled') {
      const data = getCardEligibilityData
      if (data.length > 0) {
        const isNewCard = postDataRef?.current?.isNewCard
        if (!isNewCard) {
          setShowCardEMI(true)
        } else {
          setEMIPlansAvailable(CARD_ELIGIBLE.eligible)
        }
      } else {
        setEMIPlansAvailable(CARD_ELIGIBLE.not_eligible)
      }
    } else if (getCardEligibilityState === 'rejected') {
      const isNewCard = postDataRef?.current?.isNewCard
      if (isNewCard) {
        setEMIPlansAvailable(CARD_ELIGIBLE.not_eligible)
      }
    }
  }, [getBankEmiId, getCardEligibilityData, getCardEligibilityState, isOfferApplied, navigate, validateOfferApi])

  useEffect(() => {
    if (binServiceState === 'rejected') {
      setErrMsg(staticStrings.NotValidCard)
    }
  }, [binServiceState])

  const navigatePayment = () => {
    if (isOfferApplied && applyOfferState !== 'rejected' && isValidOffer) {
      lazyApplyOffer()
    } else {
      updateIsOfferApplied(false)
      loadProcessingScreen()
    }
  }

  const handleProceed = async (isNewCard: boolean) => {
    const postData = postDataRef.current
    if (isNewCard) {
      const cardInfo = binServiceData
      const newCardEmi = postData?.newCard
      console.log('handleProceed newCardEMI', newCardEmi)
      const [expmon, expyr] = postData?.expdate.split('/')
      if (emiPlansAvailable === CARD_ELIGIBLE.eligible) {
        const products = newCardEmi.product.map((item: any) => {
          return {
            emiPlan: {
              allowZeroCost: item.emiPlan.allowZeroCost,
              bankInterestRate: item.emiPlan.bankInterestRate,
              bankInterestRatePercentage: item.emiPlan.bankInterestRatePercentage,
              productDiscount: item.emiPlan.productDiscount,
              productDiscountPercentage: item.emiPlan.productDiscountPercentage,
              programType: item.emiPlan.programType,
              additionalCashback: item.emiPlan.additionalCashback,
              scheme: item.emiPlan.scheme,
              subventionCashbackDiscount: item.emiPlan.subventionCashbackDiscount,
              subventionCashbackDiscountPercentage: item.emiPlan.subventionCashbackDiscountPercentage,
            },
            productAmount: item.productAmount,
            productId: item.productId,
          }
        })
        const newEMI = {
          bank: newCardEmi?.bank,
          tenure: newCardEmi?.tenure,
          scheme: newCardEmi?.scheme,
          metadata: newCardEmi?.metadata,
          value: newCardEmi?.emi,
          interest: newCardEmi?.interest,
          schemeSubType: newCardEmi?.schemeSubType,
          interestRate: newCardEmi?.interestRate,
          processingFee: newCardEmi?.processingFee,
          processingFeeTax: newCardEmi?.processingFeeTax,
          convenienceFee: newCardEmi?.convenienceFee,
          convenienceFeeTax: newCardEmi?.convenienceFeeTax,
          netAmount: newCardEmi?.netAmount,
          cashBack: newCardEmi?.cashBack,
          discount: newCardEmi?.discount,
          product: products,
          card: {
            number: postData?.pan,
            type: postData?.cardType,
            cvv: String(postData?.cvv),
            subType: String(cardInfo?.type ?? ''),
            ownerName: postData?.name,
            expmon: Number(expmon),
            expyr: Number(expyr),
            expDate: postData?.expdate,
            id: 0,
            scheme: String(cardInfo?.scheme ?? ''),
            isSave: postData?.isSave,
          },
          paymentType: 'EMI',
        } as EMIType
        console.log('newEMI', newEMI)
        addPaymentMethod(newEMI)
        navigatePayment()
      } else if (emiPlansAvailable === CARD_ELIGIBLE.not_eligible) {
        const newCard = {
          number: postData?.pan,
          type: postData?.cardType,
          cvv: String(postData?.cvv),
          subType: String(cardInfo?.type ?? ''),
          ownerName: postData?.name,
          expmon: Number(expmon),
          expyr: Number(expyr),
          expDate: postData?.expdate,
          id: 0,
          scheme: String(cardInfo?.scheme ?? ''),
          isSave: postData?.isSave,
          paymentType: 'Card',
        } as CardType
        addPaymentMethod(newCard)
        navigatePayment()
      }
    } else {
      const token = postData?.token
      const isNonEMICard = emiSavedCards.nonEMICards.find((item: any) => item.token === token)
      if (isNonEMICard) {
        const [expmon, expyr] = postData?.expdate.split('/')
        const newCard = {
          number: postData?.pan,
          type: postData?.cardType,
          cvv: String(postData?.cvv),
          subType: postData?.type.toString(),
          ownerName: postData?.name,
          expmon: Number(expmon),
          expyr: Number(expyr),
          expDate: postData?.expdate,
          token: postData?.token,
          id: 0,
          scheme: postData?.scheme.toString(),
          isSave: postData?.isSave,
          paymentType: 'Card',
        } as CardType
        addPaymentMethod(newCard)
        navigatePayment()
      } else if (isEMISelected) {
        const scCardEmi = postData?.scCard
        const [expmon, expyr] = postData?.expdate.split('/')

        const newEMI = {
          bank: scCardEmi?.bank,
          tenure: scCardEmi?.tenure,
          scheme: scCardEmi?.scheme,
          value: scCardEmi?.value,
          interestRate: scCardEmi?.interestRate,
          processingFee: scCardEmi?.processingFee,
          processingFeeTax: scCardEmi?.processingFeeTax,
          convenienceFee: scCardEmi?.convenienceFee,
          convenienceFeeTax: scCardEmi?.convenienceFeeTax,
          cashBack: scCardEmi?.cashBack,
          card: {
            number: postData?.pan,
            type: postData?.cardType,
            cvv: String(postData?.cvv),
            subType: postData?.type.toString(),
            ownerName: postData?.name,
            expmon: Number(expmon),
            expyr: Number(expyr),
            expDate: postData?.expdate,
            id: 0,
            scheme: postData?.scheme.toString(),
            isSave: postData?.isSave,
          },
          paymentType: 'EMI',
        } as EMIType

        addPaymentMethod(newEMI)
        navigatePayment()
      }
    }
  }

  const proceedWithPayment = ({
    cardNumber,
    cardHolderName,
    expiry,
    cvv,
    isSaveCard,
    token,
    type,
    scheme,
    isNewCard,
  }: any) => {
    if (isNewCard) {
      postDataRef.current = {
        ...postDataRef.current,
        pan: cardNumber,
        name: cardHolderName,
        cvv: cvv,
        expdate: expiry,
        isSave: isSaveCard,
        isNewCard: isNewCard,
        cardType: route?.params?.schemeSubType === '01' ? 'DEBITCARD' : 'CREDITCARD',
      }
    } else {
      postDataRef.current = {
        ...postDataRef.current,
        pan: cardNumber,
        name: cardHolderName,
        cvv: cvv,
        expdate: expiry,
        cardType: route?.params?.schemeSubType === '01' ? 'DEBITCARD' : 'CREDITCARD',
        token: token,
        type: type,
        isNewCard: isNewCard,
        isSave: isSaveCard,
        scheme: scheme,
      }
    }
    handleProceed(isNewCard)
  }

  const onCardNumbChange = (cardNumber: string) => {
    console.log('onCardNumbChange', cardNumber)
    if (isCardEMIPlanSelected) {
      setIsCardEMIPlanSelected(!isCardEMIPlanSelected)
    }
    if (emiPlansAvailable === CARD_ELIGIBLE.eligible || emiPlansAvailable === CARD_ELIGIBLE.not_eligible) {
      setEMIPlansAvailable('')
    }
    if (errMsg && cardNumber) {
      setErrMsg('')
    }
  }

  const onCardNumberBlur = useCallback(
    (cardVendor: CardVendors, cardNumber: string) => {
      //If it is new PG and if there is no error then only we have to call binservice
      binServiceApi(cardNumber)
      postDataRef.current = {
        ...postDataRef.current,
        pan: cardNumber,
        isNewCard: true,
      }
      const cardBin = cardNumber.replace(/[^0-9]/g, '').slice(0, 6)
      if (getCardEMIPlans(cardBin, route?.params?.schemeSubType ?? '00', EMI.CARD_EMI).length === 0) {
        lazyGetCardEligibility(cardBin, true, route?.params?.schemeSubType ?? '00')
      } else {
        setEMIPlansAvailable(CARD_ELIGIBLE.eligible)
      }
      console.log('cardVendor1', cardVendor)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [binServiceApi, getCardEMIPlans, lazyGetCardEligibility],
  )

  const style = styles(theme)

  if (getCardsEligibilityState === 'pending') {
    return (
      <Box flex={1} justifyContent="center" backgroundColor={theme.bodyBgColor as ColorOptions} alignItems="center">
        <Loader type="large" color={theme?.brandColor as ColorOptions} />
        <Spacer size={5} />
        <Text>Fetching the Cards</Text>
      </Box>
    )
  }

  const onDismiss = () => {
    console.log('onDismiss called')
    setShowCardEMI(false)
  }

  const onEMIPlanSelected = (item: any, isValidOffer: boolean, isNewCard: boolean) => {
    console.log('onEMIPlanSelected item', item)
    // if (isOfferApplied) {
    //   if (isValidBankEMIId(item.emiBankId)) {
    //     validateOfferApi(
    //       { card: { number: postDataRef.current.pan }, bankEmiId: item.emiBankId },
    //       { type: PAYMENT_MODE_TYPE.EMI, subType: PAYMENT_MODE_SUBTYPES.CARD },
    //     )
    //   } else {
    //     setErrMsg(staticStrings.offerError)
    //   }
    // }

    const products = item.product.map((item: any) => item)

    if (isOfferApplied) {
      if (isValidOffer) {
        setErrMsg('Offer Applied')
        setErrorColor(theme.brandColor)
      } else {
        setErrMsg(staticStrings.offerError)
        setErrorColor(theme.brandColor)
      }
    }

    const emi = {
      bank: item.bank,
      tenure: item.tenure,
      scheme: item.scheme,
      metadata: item.metadata,
      schemeSubType: item.schemeSubType,
      emi: item.emi,
      totalAmount: item.totalAmount,
      netAmount: item.netAmount,
      interest: item.interest,
      interestRate: item.interestRate,
      processingFee: item.processingFee,
      discount: item.discount,
      product: products,
      processingFeeTax: item.processingFeeTax,
      convenienceFee: item.convenienceFee,
      convenienceFeeTax: item.convenienceFeeTax,
      cashBack: item.cashBack,
    }
    postDataRef.current = {
      ...postDataRef.current,
      ...(isNewCard && {
        newCard: {
          ...emi,
        },
      }),
      ...(!isNewCard && {
        scCard: {
          ...emi,
        },
      }),
    }
    console.log('onEMIPlanSelected', postDataRef.current)
    setShowCardEMI(!showCardEMI)
    if (isNewCard) {
      setIsCardEMIPlanSelected(true)
    } else {
      setIsEMISelected(true)
    }
  }

  const onSavedCardSelected = (item: any) => {
    postDataRef.current = {
      ...postDataRef.current,
      pan: item.cardNumber,
      name: item.cardHolderName,
      cvv: item.cvv,
      expdate: item.expiry,
      cardType: item.type,
      token: item.token,
      type: item.type,
      isNewCard: item.isNewCard,
      isSave: item.isSaveCard,
      scheme: item.scheme,
      subtype: item.subtype,
      isEmiEligible: item.isEmiEligible,
    }
    setIsEMISelected(false)
  }

  const onClickSavedCardFooter = () => {
    const { token, isNewCard } = postDataRef?.current
    if (getCardEMIPlans(token, route?.params?.schemeSubType ?? '00', EMI.CARD_EMI).length === 0) {
      lazyGetCardEligibility(token, isNewCard)
    } else {
      setShowCardEMI(true)
    }
  }

  const onClickCardFooter = () => {
    setShowCardEMI(true)
  }

  const onAddNewCardClicked = () => {
    setEMIPlansAvailable('')
    postDataRef.current = {
      ...postDataRef.current,
      isNewCard: true,
    }
  }
  return (
    <Box nativeID="showListScrollBar" {...style.superView}>
      <Box height={'100%'}>
        <CreditCard
          showAddeNew={showAddeNew}
          savedCards={[]}
          errMsg={errMsg}
          amount={amount}
          errorColor={errorColor as ColorOptions}
          onPayment={proceedWithPayment}
          isValid={
            (binServiceData?.scheme &&
              (emiPlansAvailable === CARD_ELIGIBLE.eligible || emiPlansAvailable === CARD_ELIGIBLE.not_eligible)) ??
            false
          }
          onCardNumberChange={onCardNumbChange}
          onCardNumberBlur={onCardNumberBlur}
          theme={theme}
          isLoading={
            getCardEligibilityState === 'pending' || validateOfferState === 'pending' || applyOfferState === 'pending'
              ? true
              : false
          }
          showButton={isEMISelected}
          isEMI={true}
          onAddNewCard={onAddNewCardClicked}
          onCardSelected={onSavedCardSelected}
          emiCards={emiSavedCards.emiCards}
          nonEMICards={emiSavedCards.nonEMICards}
          scFotterComp={
            <SavedCardFooter
              isLoading={getCardEligibilityState === 'pending' ? true : false}
              title={
                !isEMISelected
                  ? 'Select a EMI Plan'
                  : `₹${postDataRef?.current?.scCard?.emi} X ${postDataRef?.current?.scCard?.tenureId} | ${postDataRef?.current?.scCard?.interestRate}% p.a`
              }
              isUnderLine={!isEMISelected ? false : true}
              onClick={onClickSavedCardFooter}
            />
          }
          isCardEMIEligible={emiPlansAvailable === CARD_ELIGIBLE.eligible ? true : false}
          isPayButtonEnable={isCardEMIPlanSelected}
          cardFotterComp={
            emiPlansAvailable === CARD_ELIGIBLE.eligible ? (
              <EMIEligibilityFooter
                title={
                  !isCardEMIPlanSelected
                    ? 'Select a EMI Plan'
                    : `₹${postDataRef?.current?.newCard?.emi} X ${postDataRef?.current?.newCard?.tenure} | ${postDataRef?.current?.newCard?.interestRate}% p.a`
                }
                onClick={onClickCardFooter}
              />
            ) : (
              emiPlansAvailable === CARD_ELIGIBLE.not_eligible && <EMINotEligibileFooter />
            )
          }
          onModalState={(modelState: any) => {
            setShowAddeNew(modelState)
            updateModalDisplaying(modelState)
          }}
        />
      </Box>
      {showPaymentProcessModal && (
        <MobileProcessing setShow={setShowPaymentProcessModal} show={showPaymentProcessModal} />
      )}
      {rightHeaderClicked && <CCAllEMIPlans open={rightHeaderClicked} setOpen={() => updateRightHeader(false)} />}

      {showCardEMI && (
        <CardEMIPlans
          theme={theme}
          cardNo={postDataRef.current.pan}
          schemeSubType={route?.params?.schemeSubType ?? '00'}
          onEMIPlanSelected={(item: any, isValidOffer: boolean) => {
            const isNewCard = postDataRef?.current.isNewCard
            onEMIPlanSelected(item, isValidOffer, isNewCard)
          }}
          onDismiss={onDismiss}
        />
      )}
    </Box>
  )
}

const SavedCardFooter = ({
  isLoading,
  isUnderLine,
  title,
  onClick,
}: {
  isLoading: boolean
  isUnderLine: boolean
  title: string
  onClick: () => void
}) => {
  const { theme } = useStores((root) => ({
    theme: root.config.merchantTheme,
  }))

  console.log('SavedCardFooter', title)

  return (
    <>
      <Box
        borderRadius="MEDIUM"
        height="40"
        marginBottom={14}
        justifyContent="center"
        alignItems="center"
        borderWidth={1}
        borderColor={theme.brandColor as ColorOptions}
        onPress={onClick}
        underlayColor={theme.bodyBgColor as ColorOptions}
        backgroundColor={convertHexToRGBA(theme?.brandColor, 5) as ColorOptions}
        testID={'emiSelected'}>
        {isLoading ? (
          <Loader testID="buttonLoader" color={theme.brandColor as ColorOptions} />
        ) : (
          <Text
            textDecorationLine={isUnderLine ? 'underline' : 'none'}
            fontSize={12}
            fontWeight="700"
            color={theme.brandColor as ColorOptions}>
            {title}
          </Text>
        )}
      </Box>
    </>
  )
}

const EMIEligibilityFooter = ({ title, onClick }: { title: string; onClick: () => void }) => {
  const { theme } = useStores((root) => ({
    theme: root?.config?.merchantTheme,
  }))

  const style = styles(theme)

  return (
    <Box marginBottom={10}>
      <Box
        flexDirection="row"
        underlayColor={theme?.bodyBgColor as ColorOptions}
        justifyContent="space-between"
        testID="select-plan"
        onPress={onClick}>
        <Text {...style.emiPlansEligibleText}>{title}</Text>
      </Box>
    </Box>
  )
}

const EMINotEligibileFooter = () => {
  const { theme } = useStores((root) => ({
    theme: root?.config?.merchantTheme,
  }))
  const style = styles(theme)
  return (
    <Box paddingVertical={5}>
      <Box flexDirection="row" justifyContent="space-between">
        <Text {...style.text}>Card not eligibile for emi, choose an another card</Text>
      </Box>
    </Box>
  )
}

const styles = (theme: any) => ({
  modalDataContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  } as BoxProps,
  superView: {
    flex: 1,
    backgroundColor: theme?.bodyBgColor,
    overflowY: 'auto',
  } as BoxProps,
  emiPlansEligibleText: {
    fontSize: 12,
    color: theme?.brandColor as ColorOptions,
    fontWeight: '700',
    textDecorationLine: 'underline',
  } as TextProps,
  text: {
    fontSize: 12,
    color: convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions,
    fontWeight: '700',
  } as TextProps,
  costBreakUp: {
    fontSize: 14,
    color: theme?.bodyTextColor as ColorOptions,
    fontWeight: 'bold',
  } as TextProps,
  emiBreakupValue: {
    fontSize: 12,
    color: theme?.bodyTextColor as ColorOptions,
    fontWeight: 'bold',
  } as TextProps,
  listItemHeading: (isSelected: boolean) =>
  ({
    borderBottomWidth: isSelected ? 2 : 0,
    backgroundColor: theme.bodyBgColor as ColorOptions,
    borderBottomColor: theme.brandColor as ColorOptions,
    paddingVertical: 20,
    paddingHorizontal: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
  } as BoxProps),
  headerText: (isSelected: boolean) =>
  ({
    fontSize: 12,
    color: isSelected
      ? (theme.brandColor as ColorOptions)
      : (convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions),
    fontWeight: isSelected ? 'bold' : '700',
  } as TextProps),
  listData: {
    padding: 24,
    backgroundColor: convertHexToRGBA(theme?.bodyTextColor, 5) as ColorOptions,
    underlayColor: theme?.bodyBgColor,
  } as BoxProps,
  separatorStyle: {
    height: 1,
    backgroundColor: convertHexToRGBA(theme?.bodyTextColor, 10) as ColorOptions,
  } as BoxProps,
})

export default Card
